import { FormEvent, useState } from 'react'
import { UserPlus, Shield, GraduationCap, Trash2, X, Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useDemoContext } from '../DemoContext'
import type { OrgMember } from '../../api/types'

export default function DemoMembers() {
  const { members, addMember, removeMember, activeRole, classes } = useDemoContext()
  const { t } = useTranslation('pages')

  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)

  const [assignModal, setAssignModal] = useState<number | null>(null)
  const [assignedClassIds, setAssignedClassIds] = useState<Set<number>>(new Set())

  const canManage = activeRole === 'ORG_MANAGER' || activeRole === 'ADMIN'

  if (!canManage) {
    return (
      <div className="animate-fade-in">
        <div className="page-header">
          <h2 className="page-header-title">{t('demo.members.title')}</h2>
          <p className="page-header-sub">
            {t('demo.members.accessDenied')}
          </p>
        </div>
      </div>
    )
  }

  const onAddMember = (event: FormEvent) => {
    event.preventDefault()
    const trimmedName = fullName.trim()
    const trimmedEmail = email.trim()
    if (!trimmedName || !trimmedEmail) {
      setError(t('demo.members.nameEmailRequired'))
      return
    }
    setError(null)
    const newId = Date.now()
    const created: OrgMember = {
      id: newId,
      full_name: trimmedName,
      email: trimmedEmail,
      role: 'TEACHER',
      assigned_class_count: 0,
      assigned_class_ids: [],
    }
    addMember(created)
    setFullName('')
    setEmail('')
  }

  const onRemoveMember = (member: OrgMember) => {
    removeMember(member.id)
  }

  const openAssignDialog = (member: OrgMember) => {
    setAssignModal(member.id)
    setAssignedClassIds(new Set(member.assigned_class_ids || []))
  }

  const toggleClass = (classId: number) => {
    setAssignedClassIds((prev) => {
      const next = new Set(prev)
      if (next.has(classId)) {
        next.delete(classId)
      } else {
        next.add(classId)
      }
      return next
    })
  }

  const saveAssignments = () => {
    // In demo, just close the modal (no-op save)
    setAssignModal(null)
    setAssignedClassIds(new Set())
  }

  const roleBadge = (role: string) => {
    switch (role) {
      case 'ORG_MANAGER':
        return (
          <span className="badge badge-copper">
            <Shield size={12} />
            {t('common.roles.owner')}
          </span>
        )
      case 'TEACHER':
        return (
          <span className="badge badge-info">
            <GraduationCap size={12} />
            {t('common.roles.teacher')}
          </span>
        )
      default:
        return <span className="badge badge-neutral">{role}</span>
    }
  }

  const assigningMember = members.find((m) => m.id === assignModal) ?? null

  return (
    <div className="animate-fade-in">
      <div className="page-header">
        <h2 className="page-header-title">{t('demo.members.title')}</h2>
        <p className="page-header-sub">{t('demo.members.subtitle')}</p>
      </div>

      {error && (
        <div
          className="card card-padding mb-20"
          style={{ borderLeft: '3px solid var(--color-danger)' }}
        >
          <p className="text-sm" style={{ color: 'var(--color-danger)' }}>
            {error}
          </p>
        </div>
      )}

      <div style={{ maxWidth: 800 }}>
        <div className="card card-padding mb-24">
          <h3 className="card-title mb-16">{t('demo.members.addCard')}</h3>
          <form className="flex gap-12" onSubmit={onAddMember}>
            <div className="form-group" style={{ flex: 1 }}>
              <input
                type="text"
                className="form-input"
                placeholder={t('demo.members.fullNamePlaceholder')}
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <input
                type="email"
                className="form-input"
                placeholder={t('demo.members.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <button className="btn btn-primary" type="submit">
              <UserPlus size={16} />
              {t('common.add')}
            </button>
          </form>
        </div>

        <div className="card">
          {members.length === 0 ? (
            <div className="card-padding">
              <p className="text-sm text-muted">{t('demo.members.membersEmpty')}</p>
            </div>
          ) : (
            <div className="table-wrapper">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('demo.members.colName')}</th>
                    <th>{t('demo.members.colEmail')}</th>
                    <th>{t('demo.members.colRole')}</th>
                    <th>{t('demo.members.colClasses')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => (
                    <tr key={m.id}>
                      <td className="font-semibold">{m.full_name || '-'}</td>
                      <td className="text-muted">{m.email}</td>
                      <td>{roleBadge(m.role)}</td>
                      <td>
                        <span className="chip">
                          <GraduationCap size={14} />
                          {t('demo.members.nClasses', { count: m.assigned_class_count || 0 })}
                        </span>
                      </td>
                      <td>
                        <div className="flex gap-8">
                          <button
                            className="btn btn-outline btn-xs"
                            onClick={() => openAssignDialog(m)}
                          >
                            {t('demo.members.assignClasses')}
                          </button>
                          {m.role === 'TEACHER' && (
                            <button
                              className="btn btn-danger btn-xs"
                              onClick={() => onRemoveMember(m)}
                            >
                              <Trash2 size={12} />
                              {t('demo.members.remove')}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Assign classes modal */}
      {assignModal !== null && (
        <div className="modal-overlay" onClick={() => setAssignModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">
                {t('demo.members.modalTitle', { name: assigningMember?.full_name || assigningMember?.email })}
              </span>
              <button
                className="btn btn-icon btn-ghost"
                onClick={() => setAssignModal(null)}
              >
                <X size={18} />
              </button>
            </div>
            <div className="modal-body">
              {classes.length === 0 ? (
                <p className="text-sm text-muted">
                  {t('demo.members.noClassesAvailable')}
                </p>
              ) : (
                classes.map((c) => (
                  <div
                    key={c.id}
                    className="checkbox-item"
                    onClick={() => toggleClass(c.id)}
                  >
                    <div
                      className={`checkbox-box ${assignedClassIds.has(c.id) ? 'checked' : ''}`}
                    >
                      {assignedClassIds.has(c.id) && <Check size={14} />}
                    </div>
                    <div>
                      <span className="checkbox-label">{c.name}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-outline btn-sm"
                onClick={() => setAssignModal(null)}
              >
                {t('common.cancel')}
              </button>
              <button className="btn btn-primary btn-sm" onClick={saveAssignments}>
                {t('demo.members.saveAssignments')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
